import { useState } from 'react'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Alert, Button, DialogContent, Grid, IconButton } from '@mui/material'
import { Form, Formik, FormikProps } from 'formik'

import DialogWithTitle from '../../../../../components/dialog-with-title'
import FormikTextField from '../../../../../components/formik-textfield'
import { useHost } from '../../../../../providers/app-provider'
import { useAuthentication } from '../../../../../providers/auth-provider'
import Api from '../../../../../utils/api'
import { usePasswordValidation, validate } from '../../../../../utils/password-validation'
import useStyles from '../../../index.styles'

interface FormValues {
  currentPassword: string
  newPassword: string
}

const ChangePasswordForm = ({ formikBag }: { formikBag: FormikProps<FormValues> }) => {
  const { isValid, isSubmitting, errors, dirty, touched, setFieldTouched } = formikBag
  const passwordValidation = usePasswordValidation(errors, touched)

  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false)
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false)

  return (
    <Form>
      <Grid container={true} spacing={1}>
        <Grid size={12}>
          <FormikTextField
            type={showCurrentPassword ? 'text' : 'password'}
            name="currentPassword"
            label="Current password"
            endAdornment={
              <IconButton aria-label="toggle current password visibility" onClick={() => setShowCurrentPassword(!showCurrentPassword)} size="small">
                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
            hideErrors={true}
          />
        </Grid>

        <Grid size={12}>
          <FormikTextField
            type={showNewPassword ? 'text' : 'password'}
            name="newPassword"
            label="New password"
            endAdornment={
              <IconButton aria-label="toggle new password visibility" onClick={() => setShowNewPassword(!showNewPassword)} size="small">
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
            hideErrors={true}
            onKeyDown={() => setFieldTouched('newPassword')}
          />
        </Grid>

        {passwordValidation}

        <Grid
          size={12}
          sx={{
            marginTop: 2,
          }}
        >
          <Button variant="contained" type="submit" disabled={!dirty || !isValid || isSubmitting} fullWidth={true}>
            Change Password & Log Out
          </Button>
        </Grid>
      </Grid>
    </Form>
  )
}

const ChangePasswordModal = ({ open, setOpen }: { open: boolean; setOpen: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const classes = useStyles
  const { auth, handleLogout } = useAuthentication()
  const host = useHost()

  const [error, setError] = useState<string>('')

  const initialValues = {
    currentPassword: '',
    newPassword: '',
  }

  const onSubmit = async (values: FormValues) => {
    if (!auth.user?.access_token) return
    const res = await Api.post(`${host}/api/authentication/changepassword`, {
      newPassword: values.newPassword,
      oldPassword: values.currentPassword,
    })

    if (res.ok) {
      setError('')
      handleLogout()
    } else {
      setError(res?.body?.detail ?? 'Something went wrong, please try changing password again later')
    }
  }

  return (
    <DialogWithTitle title="Password" open={open} setOpen={setOpen}>
      <DialogContent>
        {error && (
          <Alert severity="error" icon={false} sx={classes.passwordErrorAlert}>
            {error}
          </Alert>
        )}

        <Alert icon={false} severity="warning">
          You will be logged out and asked to authenticate with your new password.
        </Alert>
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
          {(formikBag) => <ChangePasswordForm formikBag={formikBag} />}
        </Formik>
      </DialogContent>
    </DialogWithTitle>
  )
}

export default ChangePasswordModal
